import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CancelMarriageCrumbs } from 'mutation/constants/cancel-marriage-crumbs';
import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import ExitModal from 'mutation/components/shared/exit-modal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FormCancelMarriage from 'mutation/components/cancel-marriage/form';
import Buttons from 'mutation/components/cancel-marriage/buttons';
import { CancelMarriageFormVM } from 'mutation/view-models/cancel-marriage';
import { validationSchema } from 'mutation/constants/cancel-marriage-validation';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import {
  checkPersonCancelMarriage,
  transformerCancelMarriageData,
} from 'mutation/utils/transformer-cancel-marriage';
import { transformerRequestCancelMarriageData } from 'mutation/utils/transformer-request-cancel-marriage';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { ItemVM } from 'shell/view-models/item';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import ErrorModal from 'mutation/components/shared/error-modal';
import PmiTitle from 'shell/components/shared/title';

const CancelMarriagePage = (): React.ReactElement => {
  const [openModal, setOpenModal] = useState(false);
  const [openError, setOpenError] = useState<boolean>(false);
  const [openWarning, setOpenWarning] = useState<boolean>(false);

  const navigate = useNavigate();

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [queryParams] = useSearchParams();
  // query params
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, CancelMarriageFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: transformerCancelMarriageData,
    }
  );

  useEffect(() => {
    const data = anpr.data as CancelMarriageFormVM;

    const canBeMutated = checkPersonCancelMarriage(data?.civilStatus?.oldStatus);

    if (!canBeMutated) {
      // persona non coniugata
      setOpenError(true);
    }
  }, [anpr.data]);

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCancelMarriage, cancelMarriage] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/mutazione/annullamento-matrimonio`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedAnprData = responseCancelMarriage.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const onCloseErrorModal = useEventCallback((value: boolean) => {
    setOpenError(value);
    navigate('/mutazioni/persona/matrimonio-annullamento');
  });

  const handleExit = useEventCallback(() => {
    setOpenModal(true);
  });

  const onConfirmExit = useEventCallback(() => {
    navigate('/mutazioni/persona/matrimonio-annullamento');
  });

  const handleSubmit = useEventCallback((values: CancelMarriageFormVM) => {
    const data = transformerRequestCancelMarriageData(values);

    (
      cancelMarriage({ data }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
    ).then((response) => {
      const castedResponse = response as SaveDataAnpr;

      setVisibility({ error: undefined, warning: undefined });

      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      if (castedResponse?.warnings?.length) {
        setOpenWarning(true);
        return;
      }

      toast.success('Operazione eseguita con successo');
      navigate('/mutazioni/persona/matrimonio-annullamento');
    });
  });

  const isLoading = !anpr.data || anpr.isLoading;

  return !isLoading ? (
    <Fragment>
      <Breadcrumbs items={CancelMarriageCrumbs} />
      <PmiTitle title="Cessazione Matrimonio" />
      {openError ? (
        <ErrorModal
          open={openError}
          onClose={onCloseErrorModal}
          message="Stato civile del soggetto incongruente con la cessazione del matrimonio"
        />
      ) : undefined}
      <Formik
        initialValues={(anpr.data || {}) as CancelMarriageFormVM}
        onSubmit={(values) => handleSubmit(values)}
        validationSchema={validationSchema}
      >
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <Buttons handleExit={handleExit} />
            </Grid>
            <Grid item xs={12} md={10}>
              <SearchMessages
                type={MessageType.Error}
                title={errorMessage.title || ''}
                messages={errorMessage.messages || []}
                visibility={errorMessage.visibility || false}
                onChangeVisibility={onVisibility}
              />
              {openWarning ? (
                <ErrorModal
                  open={openWarning}
                  onClose={() => {
                    setOpenWarning(false);
                    navigate('/mutazioni/persona/matrimonio-annullamento');
                  }}
                  message={castedAnprData?.warnings?.map((el) => el.value).join(' ')}
                />
              ) : undefined}
              <FormCancelMarriage />
            </Grid>
          </Grid>
        </Form>
      </Formik>
      <ExitModal open={openModal} onClose={setOpenModal} onConfirm={onConfirmExit} />
    </Fragment>
  ) : (
    <></>
  );
};

CancelMarriagePage.displayName = 'Cancel Marriage Page';

export default CancelMarriagePage;
